import React, { FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import { CountryCode, E164Number, parsePhoneNumber } from 'libphonenumber-js'
import Link from 'next/link'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CircleAlertIcon } from '@/atoms/Icons/CircleAlertIcon'
import { CircleCheckOutlineIcon } from '@/atoms/Icons/CircleCheckOutlineIcon'
import { GuildShieldSolid } from '@/atoms/Icons/GuildShieldSolid'
import { Image } from '@/atoms/Image'
import { IntlPhoneInput } from '@/atoms/Input/IntlPhoneInput/IntlPhoneInput'
import { AsH2, CaptionSM, LabelXS, Text } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { UNITED_STATES } from '@/constants/regions'
import { useUser } from '@/services/UserService'
import { useSafeAnalytics } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useTheaterPreferences } from '../../TheaterPreferences/TheaterPreferencesContext'
import { AutoBookStep } from '../types'

interface InfoAndPhoneStepProps {
  setCurrentStep: (step: AutoBookStep) => void
  onClose: () => void
}

const CloseButton: FC<{ onClose: () => void }> = ({ onClose }) => (
  <button
    onClick={onClose}
    className="absolute right-4 top-4 rounded-full bg-core-gray-300 p-2 transition-colors duration-200 hover:bg-core-gray-400"
    aria-label="Close"
  >
    <svg
      className="h-4 w-4 text-core-gray-900"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
)

export const InfoAndPhoneStep: FC<InfoAndPhoneStepProps> = ({ setCurrentStep, onClose }) => {
  const { t } = useTranslate('common')
  const [isValid, setIsValid] = useState(false)
  const [invalidMessage, setInvalidMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const { selectedVenueId, phoneNumber, setPhoneNumber, setAutobookEnabled } = useTheaterPreferences()

  const user = useUser()
  const { user: analyticsUser } = useSafeAnalytics()
  const defaultPhone = analyticsUser()?.traits?.phone

  useEffect(() => {
    if (defaultPhone && !phoneNumber && !isEditing) {
      setPhoneNumber(defaultPhone)
      setIsValid(true)
    }
  }, [defaultPhone, phoneNumber, setPhoneNumber, isEditing])

  const handlePhoneChange = (value?: E164Number) => {
    setPhoneNumber(value)
    setInvalidMessage('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!user.userId) {
      setInvalidMessage(t('pleaseLoginToProceed', 'Please login to proceed'))
      return
    }
    if (!isValid) {
      setInvalidMessage(t('invalidNumber', 'Invalid number'))
      return
    }

    try {
      setIsSubmitting(true)
      setAutobookEnabled(true)
      if (selectedVenueId) {
        setCurrentStep(AutoBookStep.VIEW_EXISTING_PREFERENCES)
      } else {
        setCurrentStep(AutoBookStep.THEATER_SELECTION)
      }
    } catch (e) {
      logger().error('Error in InfoAndPhoneStep handleSubmit:', { user }, e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatPhoneNumber = (phone: string) => {
    try {
      const parsed = parsePhoneNumber(phone)
      return parsed?.formatNational() || phone
    } catch (e) {
      return phone
    }
  }

  const handleEditClick = () => {
    setIsEditing(true)
    setPhoneNumber(undefined)
    setIsValid(false)
  }

  return (
    <div className="flex h-full flex-col">
      <CloseButton onClose={onClose} />
      {/* Scrollable Content */}
      <div className="flex-1 flex-col overflow-y-auto p-4 md:p-10">
        <div className="mb-6 flex justify-center">
          <Image
            src="v1742574025/angel-web/reserved-seats-illustration-03_3x"
            alt={t('reservedTheaterSeats', 'Reserved theater seats')}
            width={200}
            height={140}
            priority
          />
        </div>

        <div className="text-start">
          <Text color="core-gray-600" weight="semibold" className="text-xs font-medium uppercase tracking-wider">
            {t('premiumGuildExclusive', 'PREMIUM GUILD MEMBER EXCLUSIVE')}
          </Text>
        </div>

        <Text
          as={AsH2}
          className={classNames('mb-6 text-start', 'md:photon-heading-sm', 'photon-title-md')}
          weight="bold"
        >
          {t('saveMySeatsGetTicketsBefore', 'Save My Seats – Get Tickets Before Everyone Else')}
        </Text>

        <div className="mb-6 space-y-3">
          <div className="flex items-start gap-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center">
              <GuildShieldSolid size={22} color="core-copper" />
            </div>
            <Text color="core-gray-700">{t('exclusiveForPremium', 'Exclusive for Premium Guild members.')}</Text>
          </div>

          <div className="flex items-start gap-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center">
              <CircleCheckOutlineIcon size={22} color="core-copper" />
            </div>
            <Text color="core-gray-700">
              {t('angelBooksFirst', 'Angel books your seats first for opening weekends.')}
            </Text>
          </div>

          <div className="flex items-start gap-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center">
              <CircleCheckOutlineIcon size={22} color="core-copper" />
            </div>
            <Text color="core-gray-700">{t('textWithReserved', 'You get a text with your reserved tickets.')}</Text>
          </div>

          <div className="flex items-start gap-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center">
              <CircleCheckOutlineIcon size={22} color="core-copper" />
            </div>
            <Text color="core-gray-700">{t('confirmWithin', 'Confirm within 24 hours to lock your seats in.')}</Text>
          </div>

          <div className="flex items-start gap-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center">
              <CircleCheckOutlineIcon size={22} color="core-copper" />
            </div>
            <Text color="core-gray-700">
              {t(
                'changeYourSeatShowtimeOrTheaterAnytime',
                'Change your seat, showtime, or theater anytime up to an hour before the movie starts.',
              )}
            </Text>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="w-full shrink-0 bg-white px-4 py-1 drop-shadow-dark-5 md:rounded-b-xl md:px-10 md:py-4 md:drop-shadow-none">
        <div className="flex flex-col gap-2 py-4">
          {defaultPhone && !isEditing ? (
            <div className={classNames('w-full text-core-gray-950', 'flex items-center justify-between')}>
              <FlexColumn>
                <CaptionSM color="core-gray-600">{t('currentPhoneNumber', 'Current phone number')}</CaptionSM>
                <Text>{formatPhoneNumber(defaultPhone)}</Text>
              </FlexColumn>
              <Button variant="gray" type="button" className="py-2" onClick={handleEditClick}>
                {t('edit', 'Edit')}
              </Button>
            </div>
          ) : (
            <>
              <IntlPhoneInput
                countryCode={(user.userLocation?.countryCode as CountryCode) || UNITED_STATES}
                onPhoneValue={handlePhoneChange}
                onValidPhone={setIsValid}
                inputClassName={classNames(
                  'photon-caption-md p-4 pl-0 border border-r-0 border-y-0 border-transparent bg-core-gray-100',
                  'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent',
                  {
                    'border-l-error-500 bg-error-100': invalidMessage,
                    'focus:bg-core-gray-100': !invalidMessage,
                  },
                )}
                placeholder={t('mobilePhoneNumber', 'Mobile phone number')}
                wrapperClassName={classNames(
                  'w-full rounded-lg border-[1.5px] bg-core-gray-100 overflow-hidden text-core-gray-950',
                  {
                    'border-error-500 bg-error-100': invalidMessage,
                    'border-core-gray-300 focus-within:border-core-gray-300 focus-within:bg-core-gray-100':
                      !invalidMessage,
                  },
                )}
              />
              {invalidMessage && (
                <CaptionSM color="warm-01" className="mt-1 flex gap-1">
                  <CircleAlertIcon size={16} color="warm-01" /> {invalidMessage}
                </CaptionSM>
              )}
            </>
          )}
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <Button variant="gray" type="button" className="py-2.5 md:flex-1" onClick={onClose}>
            {t('maybeLater', 'Maybe Later')}
          </Button>
          <Button
            variant="black"
            type="button"
            onClick={handleSubmit}
            className="py-2.5 md:flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('enrolling', 'Enrolling...') : t('next', 'Next')}
          </Button>
        </div>
        <LabelXS className="mt-4 text-center" color="core-gray-600">
          <Translate
            t={t}
            i18nKey="autoBookSmsConsentTextWithLinks"
            defaults="By providing your phone number, you consent to receiving text messages from us regarding movie tickets and updates. Message and data rates may apply. Reply HELP for help or STOP to cancel. <privacyLink>Privacy Policy</privacyLink> and <termsLink>Terms of Use</termsLink>"
            components={{
              privacyLink: <Link target="_blank" href={paths.legal.privacyPolicy} className="underline" />,
              termsLink: <Link target="_blank" href={paths.legal.termsOfUse} className="underline" />,
            }}
          />
        </LabelXS>
      </div>
    </div>
  )
}
